/**
 * Irandre premium footer.
 * - Reveals the footer once, when it reaches the viewport (IntersectionObserver, no scroll listeners).
 * - Draws a hairline horizon, raises the IRANDRE letters from behind it (centre-out), then lets a
 *   single sheen of light cross the wordmark.
 * - On fine pointers a soft light follows the cursor over the wordmark.
 * - Localization selects reveal an explicit "Update" button instead of auto-submitting.
 * - Back to top, cookie preferences and newsletter feedback helpers.
 * Everything degrades to a static, fully visible footer without JS or with reduced motion.
 */
(() => {
  if (customElements.get('irandre-footer')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  // The sheen starts while the last letters (280ms + 3 × 95ms delay, 1500ms rise) are settling.
  const SHEEN_DELAY_MS = 1450;
  const SHEEN_DURATION_MS = 2200;
  const NEWSLETTER_HASH = '#IrandreFooterNewsletter';

  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class IrandreFooter extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.timers = [];
      this.intervals = [];
      this.frame = 0;
      this.sheenRunning = false;
      this.pointerInside = false;

      this.svg = this.querySelector('.irandre-wordmark');
      this.light = this.querySelector('.irandre-wordmark__light');
      this.spot = this.querySelector('.irandre-wordmark__gradient');
      this.sheenGradient = this.querySelector('.irandre-wordmark__sheen');
      this.viewBox = this.svg?.viewBox?.baseVal;

      // Coming back from a newsletter POST: show the result immediately, never animate over it.
      this.landingOnMessage =
        window.location.hash === NEWSLETTER_HASH && Boolean(this.querySelector('[data-newsletter-message]'));

      this.#setupLocalization();
      this.#setupBackToTop();
      this.#setupCookiePreferences();
      this.#setupNewsletter();

      if (this.hasAttribute('data-animate') && !reducedMotion.matches && !this.landingOnMessage) {
        this.#setupMotion();
      } else {
        this.#setupPointerLight();
      }

      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.pointerController?.abort();
      this.observer?.disconnect();
      this.#clearTimers();
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    #clearTimers() {
      this.timers.forEach((id) => window.clearTimeout(id));
      this.intervals.forEach((id) => window.clearInterval(id));
      this.timers = [];
      this.intervals = [];
    }

    /* ---------------------------------------------------------------- motion */

    #setupMotion() {
      const { signal } = this.controller;
      const wordmark = this.querySelector('.irandre-footer__wordmark');
      const threshold = window.innerHeight * 0.92;

      // Wordmark already on screen at load (short pages): leave the footer static, light only.
      if (!wordmark || wordmark.getBoundingClientRect().top < threshold) {
        this.#setupPointerLight();
        return;
      }

      // Whatever is already visible stays put; only what is still below the fold animates in.
      const pending = [...this.querySelectorAll('[data-reveal]')].filter((item) => {
        if (item.getBoundingClientRect().top < threshold) {
          item.classList.add('is-in');
          return false;
        }
        return true;
      });

      this.classList.add('is-armed');

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target;
            this.observer.unobserve(target);

            if (target === wordmark) {
              this.#playWordmark();
            } else {
              target.classList.add('is-in');
            }
          });
        },
        { threshold: 0.18, rootMargin: '0px 0px -6% 0px' }
      );

      pending.forEach((item) => this.observer.observe(item));
      this.observer.observe(wordmark);

      reducedMotion.addEventListener('change', () => reducedMotion.matches && this.#finish(), { signal });
    }

    #playWordmark() {
      this.classList.add('is-drawing');
      this.timers.push(window.setTimeout(() => this.#sheen(), SHEEN_DELAY_MS));
      // Listeners are attached right away; the spotlight simply waits for the sheen to finish.
      this.#setupPointerLight();
    }

    /** Jumps every animated element to its final state and stops every light effect. */
    #finish() {
      this.observer?.disconnect();
      this.#clearTimers();
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.sheenRunning = false;
      this.pointerController?.abort();
      this.classList.remove('is-armed', 'is-drawing', 'is-lit');
      this.querySelectorAll('[data-reveal]').forEach((item) => item.classList.add('is-in'));
    }

    /** Points the light layer at one of the two gradients (diagonal sheen or radial spotlight). */
    #useLight(gradient) {
      if (!this.light || !gradient) return;
      this.light.style.setProperty('--wordmark-letter-fill', `url(#${gradient.id})`);
    }

    #setSpot(x, y) {
      if (!this.spot) return;
      this.spot.setAttribute('cx', x.toFixed(1));
      this.spot.setAttribute('cy', y.toFixed(1));
    }

    /** One slow diagonal band of light across the letters, left to right — gold leaf catching the sun. */
    #sheen() {
      if (!this.sheenGradient || !this.viewBox || reducedMotion.matches) return;
      const { width } = this.viewBox;
      const from = -2600;
      const to = width + 1400;
      const start = performance.now();

      window.cancelAnimationFrame(this.frame);
      this.#useLight(this.sheenGradient);
      this.classList.add('is-lit');
      this.sheenRunning = true;

      const step = (now) => {
        const progress = Math.min(1, (now - start) / SHEEN_DURATION_MS);
        const x = from + (to - from) * easeInOutCubic(progress);
        this.sheenGradient.setAttribute('gradientTransform', `translate(${x.toFixed(1)} 0) skewX(-22)`);
        if (progress < 1) {
          this.frame = window.requestAnimationFrame(step);
          return;
        }
        this.frame = 0;
        this.sheenRunning = false;
        if (this.pointerInside && this.lightEnter) {
          // The cursor waited on the wordmark during the sheen: hand over to the spotlight smoothly.
          this.lightEnter();
        } else {
          this.classList.remove('is-lit');
        }
      };

      this.frame = window.requestAnimationFrame(step);
    }

    /** Desktop only: the light gently follows the cursor over the wordmark. */
    #setupPointerLight() {
      if (this.pointerController || !this.spot || !this.svg || !this.viewBox) return;
      if (!finePointer.matches || reducedMotion.matches) return;
      const link = this.querySelector('.irandre-footer__wordmark-link');
      if (!link) return;

      this.pointerController = new AbortController();
      const { signal } = this.pointerController;
      const { width, height } = this.viewBox;
      const pointer = { x: -1400, y: height / 2, tx: -1400, ty: height / 2 };

      const toSvg = (event) => {
        const rect = this.svg.getBoundingClientRect();
        const scale = width / rect.width;
        return { x: (event.clientX - rect.left) * scale, y: (event.clientY - rect.top) * scale };
      };

      const follow = () => {
        pointer.x += (pointer.tx - pointer.x) * 0.085;
        pointer.y += (pointer.ty - pointer.y) * 0.085;
        this.#setSpot(pointer.x, pointer.y);
        if (Math.abs(pointer.tx - pointer.x) > 0.5 || Math.abs(pointer.ty - pointer.y) > 0.5) {
          this.frame = window.requestAnimationFrame(follow);
        } else {
          this.frame = 0;
        }
      };

      const startFollow = () => {
        if (!this.frame && !this.sheenRunning) this.frame = window.requestAnimationFrame(follow);
      };

      // Seeds the light just outside the side nearest the cursor, so it glides in instead of jumping.
      this.lightEnter = () => {
        pointer.x = pointer.tx < width / 2 ? -900 : width + 900;
        pointer.y = pointer.ty;
        this.#setSpot(pointer.x, pointer.y);
        this.#useLight(this.spot);
        this.classList.add('is-lit');
        startFollow();
      };

      const track = (event) => {
        if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
        const point = toSvg(event);
        pointer.tx = point.x;
        pointer.ty = point.y;
        if (!this.pointerInside) {
          // Also catches a cursor that was already resting on the wordmark when it scrolled under it.
          this.pointerInside = true;
          if (!this.sheenRunning) this.lightEnter();
          return;
        }
        startFollow();
      };

      link.addEventListener('pointerenter', track, { signal });
      link.addEventListener('pointermove', track, { signal, passive: true });
      link.addEventListener(
        'pointerleave',
        () => {
          this.pointerInside = false;
          if (!this.sheenRunning) this.classList.remove('is-lit');
        },
        { signal }
      );
    }

    /* ---------------------------------------------------------- localization */

    #setupLocalization() {
      const { signal } = this.controller;
      const form = this.querySelector('.irandre-footer__locale');
      if (!form) return;
      const selects = [...form.querySelectorAll('[data-locale-select]')];

      // Compare with the server-rendered selection, not with whatever the browser restored.
      const isDirty = () => selects.some((select) => !select.selectedOptions[0]?.defaultSelected);
      const update = () => form.classList.toggle('is-dirty', isDirty());

      selects.forEach((select) => select.addEventListener('change', update, { signal }));
      window.addEventListener('pageshow', update, { signal });
      update();
    }

    /* ----------------------------------------------------------- back to top */

    #setupBackToTop() {
      const { signal } = this.controller;
      const link = this.querySelector('[data-back-to-top]');
      if (!link) return;

      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          // 'instant' (not 'auto') so the theme's CSS scroll-behavior: smooth can't override reduced motion.
          const behavior = reducedMotion.matches ? 'instant' : 'smooth';
          const wrapper = document.querySelector('.page-wrapper');
          if (wrapper && wrapper.scrollHeight > wrapper.clientHeight && wrapper.scrollTop > 0) {
            wrapper.scrollTo({ top: 0, behavior });
          }
          window.scrollTo({ top: 0, behavior });

          // Keyboard users continue from the top of the page, not from the footer.
          const main = document.getElementById('MainContent');
          if (main) {
            if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
            main.focus({ preventScroll: true });
          }
        },
        { signal }
      );
    }

    /* ------------------------------------------------------ cookie settings */

    #setupCookiePreferences() {
      const { signal } = this.controller;
      const item = this.querySelector('[data-cookie-item]');
      const button = this.querySelector('[data-cookie-preferences]');
      if (!item || !button) return;

      const available = () => typeof window.privacyBanner?.showPreferences === 'function';
      const reveal = () => {
        if (available()) item.hidden = false;
        return available();
      };

      if (!reveal()) {
        // The consent banner script can load after this one.
        let tries = 0;
        const poll = window.setInterval(() => {
          tries += 1;
          if (reveal() || tries > 10) window.clearInterval(poll);
        }, 800);
        this.intervals.push(poll);
      }

      button.addEventListener(
        'click',
        () => {
          if (available()) window.privacyBanner.showPreferences();
        },
        { signal }
      );
    }

    /* ------------------------------------------------------------ newsletter */

    #setupNewsletter() {
      const { signal } = this.controller;
      const form = this.querySelector('.irandre-footer__form');
      if (!form) return;

      const input = form.querySelector('input[type="email"]');
      const field = form.querySelector('.irandre-footer__field');
      const errorSlot = form.querySelector('[data-newsletter-error]');
      const live = form.querySelector('[data-newsletter-live]');
      const message = form.querySelector('[data-newsletter-message]');

      if (this.landingOnMessage && message) {
        // After a server error the invalid field takes focus (it is described by the error);
        // after a success the confirmation itself does.
        const target = input && input.getAttribute('aria-invalid') === 'true' ? input : message;
        window.requestAnimationFrame(() => {
          message.scrollIntoView({ block: 'center', behavior: 'instant' });
          target.focus({ preventScroll: true });
        });
      }

      if (!input || !errorSlot) return;

      const describedBy = () => (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
      const describeError = (on) => {
        const rest = describedBy().filter((id) => id !== errorSlot.id);
        input.setAttribute('aria-describedby', (on ? [errorSlot.id, ...rest] : rest).join(' '));
      };

      const clearError = () => {
        if (input.getAttribute('aria-invalid') !== 'true') return;
        input.removeAttribute('aria-invalid');
        describeError(false);
        field?.classList.remove('irandre-footer__field--error');
        errorSlot.hidden = true;
        if (live) live.textContent = '';
      };

      form.addEventListener(
        'submit',
        (event) => {
          const value = input.value.trim();
          input.value = value;
          if (value && input.checkValidity()) return;

          event.preventDefault();
          const hadFocus = document.activeElement === input;
          errorSlot.hidden = false;
          field?.classList.add('irandre-footer__field--error');
          input.setAttribute('aria-invalid', 'true');
          describeError(true);
          input.focus();

          // Submitted with Enter from the field: focus doesn't move, so announce the error explicitly.
          // (A click on Subscribe moves focus back to the field, which already reads the description.)
          if (hadFocus && live) {
            live.textContent = '';
            window.requestAnimationFrame(() => {
              live.textContent = errorSlot.textContent.trim();
            });
          }
        },
        { signal }
      );

      input.addEventListener('input', clearError, { signal });
    }
  }

  customElements.define('irandre-footer', IrandreFooter);
})();
